import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "./supabase";
import { apiGet } from "./api";

export type HqMe = {
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  is_active: boolean;
  modules: string[];
};

type AuthState = {
  status: "loading" | "signed-out" | "signed-in" | "not-provisioned" | "error";
  me: HqMe | null;
  /** Email on the live Supabase session. Useful for diagnostics before `hq_me` resolves. */
  sessionEmail: string | null;
  error: string | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  hasModule: (slug: string) => boolean;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [me, setMe] = useState<HqMe | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadMe() {
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        setStatus("signed-out");
        setMe(null);
        setSessionEmail(null);
        return;
      }
      setSessionEmail(data.session.user?.email ?? null);
      const fresh = await apiGet<HqMe>("/me");
      setMe(fresh);
      setStatus("signed-in");
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.includes("not_provisioned")) {
        setStatus("not-provisioned");
      } else {
        setError(msg);
        setStatus("error");
      }
    }
  }

  useEffect(() => {
    loadMe();
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setMe(null);
        setStatus("signed-out");
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        loadMe();
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  async function signIn() {
    setError(null);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        queryParams: { prompt: "select_account" },
      },
    });
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const value: AuthState = {
    status,
    me,
    sessionEmail,
    error,
    signIn,
    signOut,
    hasModule: (slug) => !!me && (me.is_admin || me.modules.includes(slug)),
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
